import { DOCUMENT } from '@angular/common';
import { Component, Inject, Input, NgZone, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { Values } from '@mode-switch/model';
import {
  BasicChannel,
  EncDualAxisXField,
  EncDualAxisYField,
  EncField,
  EncFieldGroup,
  Encoding,
  EncodingResult,
} from '@mode-vegas/vegas';
import { ChartFacade, DragFieldInfo, SelectedChartService } from '@mode/charts/contract';
import { EncodingHelpers, SharedAxisHelpers } from '@mode/charts/util-vegas';
import { BinSizeTypes, ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { FLAMINGO_RESERVED_REFS } from '@mode/shared/util-js';
import { CompiledField } from '@mode/shared/util-switch';
import { PillActionType } from 'libs/shared/contract-common/src/lib/charts';
import { DndDropEvent } from 'ngx-drag-drop';
import { Observable, Subscription, of } from 'rxjs';
import { first } from 'rxjs/operators';
import { DragEndData, DragStartData, FieldDropData, OnDropEvent } from '../helpers/field-helpers';

@Component({
  selector: 'mode-encoding-builder-vegas',
  templateUrl: './encoding-builder-vegas.component.html',
  styleUrls: ['./encoding-builder-vegas.component.scss'],
})
export class EncodingBuilderVegasComponent implements OnInit, OnDestroy, OnChanges {
  @Input({ required: true }) encoding!: Encoding;
  @Input() observables: any;
  @Input({ required: true }) chartService!: SelectedChartService;
  @Input({ required: true }) model!: FlamingoTypes.ModeModel;
  @Input() isExploring = false;
  @Input({ required: true }) chartType!: ChartTypes.ChartType;

  queryToken$: Observable<string> | undefined;
  view$: Observable<FlamingoTypes.ModeView> | undefined;
  vizToken$: Observable<string> | undefined;
  fieldFormatMap$: Observable<ChartTypes.FieldColorMap> | undefined;
  format$: Observable<ChartTypes.ChartFormat> | undefined;
  binSizeToFieldMap$: Observable<BinSizeTypes.BinSizeToFieldMap> | undefined;

  view: FlamingoTypes.ModeView | undefined;
  viewSubscription: Subscription | undefined;
  chartSubscription: Subscription | undefined;
  fieldFormatMapSubscription: Subscription | undefined;

  dragInfo: DragFieldInfo | undefined;
  isDragValidForValues = false;

  columns: ReadonlyArray<EncField | EncDualAxisXField> = [];
  groupedColumns: ReadonlyArray<EncField | EncFieldGroup> = [];
  rows: ReadonlyArray<EncField | EncDualAxisYField> = [];
  groupedRows: ReadonlyArray<EncField | EncFieldGroup> = [];
  groupIdToFieldMap = new Map<string, EncField[]>();

  isChartEncodingComplete = false;
  hasMeasureValues = false;

  fieldFormatMaps: ChartTypes.FieldFormatMap | undefined;

  constructor(
    private chartFacade: ChartFacade,
    @Inject(DOCUMENT) private document: Document,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.queryToken$ = this.observables.queryToken$;
    this.view$ = this.observables.view$;
    this.vizToken$ = this.isExploring ? of(FLAMINGO_RESERVED_REFS.Exploration) : this.observables.token$;
    this.viewSubscription = this.view$?.subscribe((view) => (this.view = view));
    this.fieldFormatMap$ = this.observables.fieldFormats$;
    this.format$ = this.observables.format$;
    this.binSizeToFieldMap$ = this.observables.binSizeToFieldMap$;

    this.chartSubscription = this.chartFacade.dragField$.subscribe((dragInfo: DragFieldInfo) => {
      this.dragInfo = dragInfo;
      this.isDragValidForValues = this.dragInfo ? EncodingHelpers.canAddOrMoveToValues(dragInfo.formula) : false;
    });

    this.ngZone.runOutsideAngular(() => {
      this.document.addEventListener('dragover', this.documentDragOver);
    });

    this.isChartEncodingComplete = EncodingHelpers.isChartEncodingComplete(this.encoding, this.chartType);

    this.fieldFormatMapSubscription = this.observables.fieldFormats$.subscribe(
      (fieldFormats: ChartTypes.FieldFormatMap) => {
        this.fieldFormatMaps = fieldFormats;
      }
    );
  }

  ngOnDestroy(): void {
    this.chartSubscription?.unsubscribe();
    this.fieldFormatMapSubscription?.unsubscribe();
    this.viewSubscription?.unsubscribe();

    this.document.removeEventListener('dragover', this.documentDragOver);
  }

  ngOnChanges() {
    this.isChartEncodingComplete = EncodingHelpers.isChartEncodingComplete(this.encoding, this.chartType);
    this.hasMeasureValues = EncodingHelpers.isInEncoding(this.encoding, Values.Key);
    this.columns = this.buildColumns();
    this.groupedColumns = this.groupFields(this.columns);
    this.rows = this.buildRows();
    this.groupedRows = this.groupFields(this.rows);
    this.groupIdToFieldMap = this.buildGroupIdToFieldMap();
  }

  documentDragOver(event: DragEvent) {
    event.preventDefault();
  }

  onFieldDrop({ field, channel }: FieldDropData) {
    if (this.dragInfo == null) {
      return;
    }
    const formula = this.dragInfo.formula;
    const fieldIds = this.dragInfo.fields;
    this.chartFacade.endFieldDrag();

    if (!formula || !field.id) {
      return;
    }

    let newEncoding;

    if (formula.isField) {
      if (formula.alias === field.id) {
        // inserting field in the same location as it was
        return;
      }
      // move field and dispatch
      newEncoding = EncodingHelpers.replaceAndMoveVegasField(
        this.encoding,
        fieldIds ? fieldIds : (formula.alias ?? ''),
        channel,
        field.id
      );
    } else {
      newEncoding = EncodingHelpers.replaceAndAddNewVegasField(this.encoding, formula, field.id, channel);
    }

    if (newEncoding != null) {
      this.chartService.dispatchEncodingUpdate(newEncoding);
    }
  }

  moveGroup(event: DndDropEvent, channel: BasicChannel, groupId: string) {
    const groupedFields = channel === 'row' ? this.groupedRows : this.groupedColumns;
    const dropIndex = event.index ?? groupedFields.length;
    const atField = dropIndex < groupedFields.length ? groupedFields[dropIndex] : undefined;

    const getIdForFieldOrGroup = (fieldOrGroup: EncField | EncFieldGroup) => {
      if (this.isEncField(fieldOrGroup)) {
        return fieldOrGroup.id;
      }
      const fieldList = channel === 'row' ? this.rows : this.columns;
      return fieldList.find((f) => f.groupId === fieldOrGroup.id)?.id;
    };

    const at = atField != null ? getIdForFieldOrGroup(atField) : undefined;

    const newEncoding = SharedAxisHelpers.moveSharedAxis(this.encoding, groupId, channel, at);
    this.chartService.dispatchEncodingUpdate(newEncoding);
  }

  onDrop($event: OnDropEvent) {
    const { event, channel, attr } = $event;
    const formula = attr ? attr : this.dragInfo?.formula;
    const fieldIds = this.dragInfo?.fields;
    const encoding = this.encoding;
    const view = this.view;
    const groupId = this.dragInfo?.groupId;

    this.chartFacade.endFieldDrag();

    if (view == null) {
      return;
    }

    // Bail on empty formula
    if (formula == null) {
      if (groupId != null) {
        this.moveGroup(event, channel, groupId);
      }
      return;
    }

    // Bail if it's not a channel we need to handle drops for
    if (!['column', 'row', 'value'].includes(channel)) {
      return;
    }

    const dropIndex: number | undefined = this.getAdjustedDropIndex(event.index, channel);
    const newEncoding = EncodingHelpers.addOrMoveVegasFieldToBasicChannel(
      view,
      encoding,
      channel,
      formula,
      dropIndex,
      fieldIds
    );

    if (newEncoding != null) {
      this.chartService.dispatchEncodingAndFormatUpdate(
        EncodingHelpers.updateAutomaticMarkType(view, encoding, newEncoding),
        channel === 'column' || channel === 'row' ? {} : undefined
      );
    }
  }

  getAdjustedDropIndex(eventIndex: number | undefined, channel: string) {
    if (eventIndex === undefined || (channel !== 'column' && channel !== 'row')) {
      return eventIndex;
    }
    if (eventIndex <= 0) {
      return 0;
    }

    const fieldList = channel === 'column' ? this.groupedColumns : this.groupedRows;
    const ungroupedFields = channel === 'column' ? this.columns : this.rows;
    const slicedFields = fieldList.slice(0, eventIndex);
    const fieldCount = slicedFields.reduce((count, field) => {
      if (this.isEncField(field)) {
        return ++count;
      }
      const groupFields = ungroupedFields.filter((f) => f.groupId === field.id);
      return count + groupFields.length;
    }, 0);
    return fieldCount;
  }

  onDragStart({ field, source }: DragStartData) {
    if (this.isEncField(field)) {
      if (this.view && [...this.columns, ...this.rows, ...this.encoding.value].some((f) => f.id === field.id)) {
        const compiledField = CompiledField.fromEncField(this.view, field as EncField);
        this.chartFacade.startFieldDrag(compiledField, source);
      }
    } else if (this.isEncFieldGroup(field)) {
      this.chartFacade.startFieldDrag(null, source, undefined, field.id);
    }
  }

  onDragEnd({ field }: DragEndData) {
    if (this.dragInfo == null) {
      return;
    }
    if (this.isEncField(field) && this.dragInfo.formula) {
      this.removeField(
        { $field: field },
        this.dragInfo.source === 'column' || this.dragInfo.source === 'row' ? {} : undefined
      );
      this.chartFacade.endFieldDrag();
    } else if (this.isEncFieldGroup(field) && field.id && this.dragInfo.groupId === field.id) {
      const newEncoding = SharedAxisHelpers.removeSharedAxis(this.encoding, field.id);
      this.chartService.dispatchEncodingUpdate(newEncoding);
      this.chartFacade.endFieldDrag();
    }
  }

  onFieldDragEnd() {
    this.chartFacade.endFieldDrag();
  }

  isDragging() {
    return !!this.dragInfo?.formula || !!this.dragInfo?.groupId;
  }

  isDualAxis(field: EncField) {
    return EncodingHelpers.isDualAxisEncoding(this.encoding, field.id ?? '');
  }

  isEncField(field: EncField | EncFieldGroup) {
    return 'formula' in field;
  }

  isEncFieldGroup(field: EncField | EncFieldGroup) {
    return 'type' in field;
  }

  getGroupEncFields(group: EncFieldGroup) {
    return this.groupIdToFieldMap.get(group.id) ?? [];
  }

  fieldUpdated($event: { $result: EncodingResult; $formats?: ChartTypes.FieldFormatMap }) {
    const { $result, $formats } = $event;
    if ($formats != null) {
      this.observables.fieldFormats$.pipe(first()).subscribe((fieldFormats: ChartTypes.FieldFormatMap) => {
        // Merge the partial format update with the existing FieldFormatMap. The update in chart service
        // does a replace.
        this.chartService.dispatchEncodingAndFormatUpdate($result, undefined, { ...(fieldFormats ?? {}), ...$formats });
      });
    } else {
      this.chartService.dispatchEncodingUpdate($result, PillActionType.ChangePillFormula);
    }
  }

  removeField($event: { $field: EncField }, facetSizes?: ChartTypes.FacetSizeMap) {
    const { $field } = $event;
    if ($field.id == null) {
      return;
    }
    let encResult = EncodingHelpers.removeFieldFromEncoding(this.encoding, this.chartType, $field.id); // can use any chart type in the shape

    // Update the automatic mark type and the facet size map if needed and dispatch the update event.
    // "pipe(first()).subscribe()" executes the subscription callback once, then removes the subscription.
    this.view$?.pipe(first()).subscribe((view) => {
      encResult = EncodingHelpers.updateAutomaticMarkType(view, this.encoding, encResult);
      this.chartService.dispatchEncodingAndFormatUpdate(encResult, facetSizes);
    });
  }

  removeSort($event: { $source: string }) {
    const { $source } = $event;
    this.chartService.removeSort($source);
  }

  toggleDualAxis($event: { $field: CompiledField; $sibling: EncField | undefined; $channel: 'row' | 'column' }) {
    const { $field, $sibling, $channel } = $event;

    if ($field && $field.isField && $field.alias) {
      if (EncodingHelpers.isDualAxisEncoding(this.encoding, $field.alias)) {
        // toggle off dual axis
        const encResult = EncodingHelpers.updateDualAxisField(this.encoding, $field.alias);
        this.chartService.dispatchEncodingUpdate(encResult);
      } else if ($sibling?.id) {
        const series2Mark = EncodingHelpers.getEncSeriesMarkById(this.encoding, $field.alias);
        const seriesMark = EncodingHelpers.getEncSeriesMarkById(this.encoding, $sibling.id);

        if (series2Mark && seriesMark) {
          this.chartService.moveFieldAndMakeDual(this.encoding, $field, $channel, seriesMark, series2Mark);
        }
      }
    }
  }

  isValuesEmpty(): boolean {
    return this.encoding?.value.length === 0;
  }

  buildColumns() {
    return this.encoding != null ? EncodingHelpers.buildBasicColumns(this.encoding) : [];
  }

  buildRows() {
    return this.encoding != null ? EncodingHelpers.buildBasicRows(this.encoding) : [];
  }

  buildGroupIdToFieldMap() {
    const mapping = new Map<string, EncField[]>();
    const axisFields = [...this.encoding.x, ...this.encoding.y, ...this.encoding.y2, ...this.encoding.x2];
    axisFields.forEach((field) => {
      if (field.groupId != null) {
        if (!mapping.has(field.groupId)) {
          mapping.set(field.groupId, []);
        }
        const fields = mapping.get(field.groupId);
        fields?.push(field);
      }
    });
    return mapping;
  }

  groupFields(fields: ReadonlyArray<EncField>) {
    return fields.reduce(
      (fieldList, field) => {
        const fieldGroup = this.encoding.groups?.find((g) => g.id === field.groupId);
        if (fieldGroup) {
          if (fieldList.some((f) => f.id === field.groupId)) {
            return fieldList;
          }
          fieldList.push(fieldGroup);
        } else {
          fieldList.push(field);
        }
        return fieldList;
      },
      [] as Array<EncField | EncFieldGroup>
    );
  }

  getBinSize($event: { columnName: string; token: string }) {
    this.chartService.getBinSize($event.token, $event.columnName);
  }
}
